import { useQuery } from '@tanstack/react-query';

import { catalogQuery } from '../catalogQuery';
import { missingParts } from '../missingParts';

/**
 * What one installed workflow names and this workspace no longer holds
 * (spec 107 US2).
 *
 * For a surface outside the Library — the board is where somebody creates the
 * task that will run this workflow, and it is the last moment the gap can be
 * pointed out before work is started on a sandbox that will quietly lack a
 * skill.
 *
 * Deliberately quiet about everything else. A library that cannot be read, a
 * workflow the library does not hold, a workspace that never used the Library
 * at all: each answers "nothing missing", because a board must not grow a
 * warning out of the Library being unavailable.
 *
 * Its own freshness, not the Library page's. That page re-reads on every visit
 * because it is about to be acted on; a board wants an answer that is merely
 * recent, and the same question asked twice a minute from a board is waste.
 *
 * What it asks and what it stores are not its own, though. Both live in
 * `catalogQuery`, because this hook and the Library page share one cache entry
 * and once had one `queryFn` each: visiting the Library and then opening a board
 * handed this hook an envelope where it expected an array, and the board died on
 * `items.find`. Freshness is a per-reader choice; the shape is not.
 */
export function useWorkflowGaps(workspaceId: string, workflowKey: string) {
  const query = useQuery({
    ...catalogQuery(workspaceId),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const items = query.data?.items ?? [];
  const workflow = items.find(
    (item) => item.kind === 'workflow' && item.key === workflowKey,
  );
  return workflow ? missingParts(workflow, items) : [];
}
